import React from 'react';
import './ProfileCompletionBar.css';

interface ProfileCompletionBarProps {
  score: number;               // 0-100
  variant?: 'compact' | 'full'; // full shows percentage number
  className?: string;
}

/**
 * Visual progress bar showing profile completion percentage
 * Uses color coding: red (0-30) -> orange (30-70) -> green (70-100)
 */
const ProfileCompletionBar: React.FC<ProfileCompletionBarProps> = ({
  score,
  variant = 'full',
  className = ''
}) => {
  // Clamp score between 0 and 100
  const clampedScore = Math.max(0, Math.min(100, score));

  // Determine color based on score
  const getColorClass = (): string => {
    if (clampedScore < 30) return 'profile-completion-bar--danger';
    if (clampedScore < 70) return 'profile-completion-bar--warning';
    return 'profile-completion-bar--success';
  };

  return (
    <div className={`profile-completion-bar profile-completion-bar--${variant} ${className}`}>
      {variant === 'full' && (
        <div className="profile-completion-bar__label">
          <span className="profile-completion-bar__text">Profile completion</span>
          <span className={`profile-completion-bar__percentage ${getColorClass()}`}>
            {clampedScore}%
          </span>
        </div>
      )}
      <div className="profile-completion-bar__track">
        <div
          className={`profile-completion-bar__fill ${getColorClass()}`}
          style={{ width: `${clampedScore}%` }}
          role="progressbar"
          aria-valuenow={clampedScore}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-label={`Profile completion: ${clampedScore}%`}
        />
      </div>
      {variant === 'compact' && (
        <span className={`profile-completion-bar__compact-value ${getColorClass()}`}>
          {clampedScore}%
        </span>
      )}
    </div>
  );
};

export default ProfileCompletionBar;
